/**
 * Custom hook for document source connection operations
 * Handles CRUD and test/sync operations for connections
 */

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { DocumentSourceUserConnectionRequest } from '@/api/ApiClient';
import { apiClient } from '@/config/apiConfig';
import { log } from '@/utils/errors/logger';

interface UseConnectionOperationsProps {
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  onDataChanged: () => Promise<void>;
}

export const useConnectionOperations = ({
  showSuccess,
  showError,
  onDataChanged,
}: UseConnectionOperationsProps) => {
  const { t } = useTranslation();

  const saveConnection = useCallback(async (
    data: DocumentSourceUserConnectionRequest,
    editingConnectionId?: string
  ): Promise<void> => {
    try {
      if (editingConnectionId) {
        await apiClient.updateUserConnection(editingConnectionId, data);
        showSuccess(t('documentSources.messages.connectionUpdated'));
      } else {
        await apiClient.createUserConnection(data);
        showSuccess(t('documentSources.messages.connectionCreated'));
      }
      await onDataChanged();
    } catch (error: unknown) {
      log.error('Failed to save connection', { error });
      const apiError = error as { response?: { data?: { detail?: string } } };
      showError(apiError.response?.data?.detail ?? t('documentSources.errors.saveFailed'));
    }
  }, [t, showSuccess, showError, onDataChanged]);

  const deleteConnection = useCallback(async (connectionId: string): Promise<void> => {
    try {
      await apiClient.deleteUserConnection(connectionId);
      showSuccess(t('documentSources.messages.connectionDeleted'));
      await onDataChanged();
    } catch (error: unknown) {
      log.error('Failed to delete connection', { error });
      showError(t('documentSources.errors.deleteFailed'));
    }
  }, [t, showSuccess, showError, onDataChanged]);

  const testConnection = useCallback(async (connectionId: string): Promise<void> => {
    try {
      // Dedicated test endpoint is not present in current contract.
      // Use fetch-by-id as a lightweight connectivity check.
      await apiClient.getUserConnection(connectionId);
      showSuccess(t('documentSources.messages.testSuccess'));
    } catch (error: unknown) {
      log.error('Failed to test connection', { error });
      showError(t('documentSources.errors.testFailed'));
    }
  }, [t, showSuccess, showError]);

  return {
    saveConnection,
    deleteConnection,
    testConnection,
  };
};
